// BUILD-042 cycle 13 - ONE presentational layout for the checklist area, used by the guided-setup Onboarding step, the employee's post-setup Onboarding tab and the
// Offboarding tab (kind decides only the wording). Authority: the Owner's "Checklist presentation contract" (decision 2026-10-24 final local repairs).
//
//   Header row : heading on the left; on the right, OUTSIDE and ABOVE the assigned-checklists card, a smaller "Manage checklist templates" link next to the primary
//                "+ Assign checklist" button (the link stacks above the button on narrow widths).
//   Assign     : a compact panel opens BENEATH the header (above the card). Cancel closes it with nothing sent. Success closes it, the caller refreshes the list, and a
//                short transient confirmation (role=status, ~5s) appears. An error keeps the panel open (the form keeps every value).
//   Card       : "Assigned checklists" holds ONLY assigned checklists. Its empty state (nothing assigned, list readable) is the centered document icon + the two lines.
//                A populated card is never replaced by the empty state; loading / error / denied are drawn by the caller inside the card, never as empty.
//
// Presentation only: no request is made here, and no authorization decision (the server re-authorizes every call).
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { EMPTY_HINT, EMPTY_TITLE, NOTICE_MS, type ChecklistKind } from './checklistModel';

/** A short-lived confirmation. Showing a new one replaces the old; it clears itself after `ms` and on unmount. */
export function useTransientNotice(ms: number = NOTICE_MS) {
  const [notice, setNotice] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  const clear = useCallback(() => { if (timer.current) window.clearTimeout(timer.current); timer.current = null; setNotice(null); }, []);
  const show = useCallback((text: string) => {
    if (timer.current) window.clearTimeout(timer.current);
    setNotice(text);
    timer.current = window.setTimeout(() => { timer.current = null; setNotice(null); }, ms);
  }, [ms]);
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);
  return { notice, show, clear };
}

/** The live region is always mounted (so assistive technology announces the text when it appears). */
export function TransientNotice({ text, testId = 'checklist-notice', className = '' }: { text: string | null; testId?: string; className?: string }) {
  return (
    <div role="status" aria-live="polite" className={className}>
      {text && <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-md px-3 py-1.5 inline-block" data-testid={testId}>{text}</p>}
    </div>
  );
}

function DocumentIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-9 h-9" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 3h7l4 4v14H7z" /><path d="M14 3v4h4" /><path d="M10 13h5M10 17h3" />
    </svg>
  );
}

export interface AssignPanelApi {
  /** Close the panel (Cancel, or after a success). Nothing is sent by closing. */
  close: () => void;
  /** Close the panel and show the transient confirmation. */
  succeeded: (text: string) => void;
}

export function ChecklistWorkArea({ kind, manageLink, canOpen, openHint, hideActions = false, assignPanel, children, testId, intro }: {
  kind: ChecklistKind;
  /** The "Manage checklist templates" link element (its destination and return path belong to the caller). Omit when the caller may not manage templates. */
  manageLink?: ReactNode;
  /** false: the Assign button is disabled (templates not loaded / none exist / not permitted); `openHint` says why. */
  canOpen: boolean;
  openHint?: string;
  /** true: no Assign / Manage controls (the area itself is unavailable). */
  hideActions?: boolean;
  assignPanel: (api: AssignPanelApi) => ReactNode;
  children: ReactNode;
  testId?: string;
  /** One short sentence under the heading (optional). */
  intro?: string;
}) {
  const [open, setOpen] = useState(false);
  const { notice, show } = useTransientNotice();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const heading = kind === 'offboarding' ? 'Offboarding checklists' : 'Onboarding checklists';

  const close = useCallback(() => { setOpen(false); window.setTimeout(() => buttonRef.current?.focus(), 0); }, []);
  const succeeded = useCallback((text: string) => { setOpen(false); show(text); }, [show]);
  // The area losing the right to open (for example the templates reload and none are left) closes a panel that is open.
  useEffect(() => { if (!canOpen && open) setOpen(false); }, [canOpen, open]);

  return (
    <section aria-label={heading} data-testid={testId ?? `${kind}-checklist-area`}>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 mb-3">
        <div className="min-w-0 flex-1 basis-56">
          <h2 className="text-base font-semibold text-gray-900">{heading}</h2>
          {intro && <p className="text-xs text-gray-500 mt-0.5">{intro}</p>}
        </div>
        {!hideActions && (
          <div className="ml-auto flex flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-3" data-testid="checklist-actions">
            {manageLink}
            <button
              ref={buttonRef} type="button" aria-expanded={open} aria-controls="checklist-assign-panel"
              className="inline-flex items-center justify-center gap-1 text-sm font-medium bg-[#4AABDE] text-white rounded-md px-4 py-2 hover:bg-[#3b96c4] disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!canOpen || open} title={!canOpen ? openHint : undefined} onClick={() => setOpen(true)}
              data-testid="checklist-assign-open"
            >
              <span aria-hidden="true">+</span> Assign checklist
            </button>
          </div>
        )}
      </div>

      <TransientNotice text={notice} className={notice ? 'mb-3' : ''} />

      {open && !hideActions && (
        <div id="checklist-assign-panel" className="mb-4">{assignPanel({ close, succeeded })}</div>
      )}

      {children}
    </section>
  );
}

/** The "Assigned checklists" card. `empty` is true ONLY when the list was read successfully and holds nothing for this process kind. */
export function AssignedChecklistsCard({ kind, empty, children }: { kind: ChecklistKind; empty: boolean; children?: ReactNode }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-5" data-testid={`${kind}-checklist-card`}>
      <h3 className="text-sm font-semibold text-gray-900 mb-3">Assigned checklists</h3>
      {empty ? (
        <div className="flex flex-col items-center text-center gap-1.5 py-8 text-gray-500" data-testid="checklist-empty">
          <span className="text-gray-400"><DocumentIcon /></span>
          <p className="text-sm font-medium text-gray-700">{EMPTY_TITLE[kind]}</p>
          <p className="text-sm text-gray-500">{EMPTY_HINT}</p>
        </div>
      ) : children}
    </div>
  );
}
